import { useState, useEffect, useCallback, useRef } from "react";
import { useDebounce } from "@/hooks/useDebounce";
import { recordService } from "@/integrations/records/recordService";
import { useNotifications } from "@/contexts/NotificationContext";
import { useAuth } from "@/contexts/UnifiedAuthContext";

interface UseEditableProcessedTextOptions {
  initialValue: string;
  recordId: string;
  onSuccess?: (newValue: string) => void;
  autoSaveDelay?: number;
  enabled?: boolean;
}

export interface EditableProcessedTextState {
  isEditing: boolean;
  isSaving: boolean;
  hasChanges: boolean;
  error: string | null;
  currentValue: string;
}

export const useEditableProcessedText = ({
  initialValue,
  recordId,
  onSuccess,
  autoSaveDelay = 30000, // 30 seconds
  enabled = true,
}: UseEditableProcessedTextOptions) => {
  const { user } = useAuth();
  const { addNotification } = useNotifications();

  const [state, setState] = useState<EditableProcessedTextState>({
    isEditing: false,
    isSaving: false,
    hasChanges: false,
    error: null,
    currentValue: initialValue,
  });

  const [editingValue, setEditingValue] = useState(initialValue);
  
  // Ref to keep track of the most recent currentValue
  const currentValueRef = useRef(initialValue);
  
  // Debounced value for auto-save
  const debouncedValue = useDebounce(editingValue, autoSaveDelay);

  // Update current value when initial value changes
  useEffect(() => {
    // Always sync ref with initial value
    currentValueRef.current = initialValue;
    
    setState(prev => ({
      ...prev,
      currentValue: initialValue,
    }));
    
    // Update editing value only if not currently editing
    if (!state.isEditing) {
      setEditingValue(initialValue);
    }
  }, [initialValue, state.isEditing, state.currentValue]);

  // Auto-save when debounced value changes during editing
  useEffect(() => {
    if (
      state.isEditing &&
      state.hasChanges && // Только если есть изменения
      debouncedValue !== state.currentValue &&
      debouncedValue.trim() !== "" &&
      enabled
    ) {
      saveValue(debouncedValue, true); // Silent auto-save
    }
  }, [debouncedValue, state.isEditing, state.currentValue, state.hasChanges, enabled]);

  const startEditing = useCallback(() => {
    if (!enabled) return;
    
    // Используем ref для получения актуального значения и сбрасываем состояние редактирования
    const currentValue = currentValueRef.current;
    setEditingValue(currentValue);
    setState(prev => ({
      ...prev,
      isEditing: true,
      hasChanges: false, // Важно: сбрасываем флаг изменений
      error: null,
    }));
  }, [enabled, initialValue, state.currentValue, editingValue]);

  const cancelEditing = useCallback(() => {
    // Используем ref для получения актуального значения
    setEditingValue(currentValueRef.current);
    setState(prev => ({
      ...prev,
      isEditing: false,
      hasChanges: false,
      error: null,
    }));
  }, []);

  const saveValue = useCallback(
    async (value: string, silent = false) => {
      if (!enabled || !user) return;

      setState(prev => ({ ...prev, isSaving: true, error: null }));

      try {
        await recordService.updateRecordProcessedText(recordId, value);

        // Обновляем ref перед состоянием
        currentValueRef.current = value;
        
        setState(prev => ({
          ...prev,
          isSaving: false,
          hasChanges: false,
          currentValue: value,
          isEditing: false,
        }));

        setEditingValue(value);
        
        if (!silent) {
          addNotification({
            userId: user.id,
            type: "success",
            message: "Обработанный текст сохранён",
          });
        }
        
        // Call onSuccess only after successful save
        onSuccess?.(value);
      } catch (error) {
        console.error("Error saving processed text:", error);
        
        const errorMessage = 
          error instanceof Error 
            ? error.message 
            : "Не удалось сохранить изменения";

        setState(prev => ({
          ...prev,
          isSaving: false,
          error: errorMessage,
        }));

        if (!silent) {
          addNotification({
            userId: user?.id || "",
            type: "error",
            message: errorMessage,
          });
        }
        
        // Don't call onSuccess on error
        return;
      }
    },
    [enabled, user, recordId, onSuccess, addNotification]
  );

  const updateEditingValue = useCallback((value: string) => {
    setEditingValue(value);
    setState(prev => ({
      ...prev,
      hasChanges: value !== currentValueRef.current,
      error: null,
    }));
  }, []);

  // Prevent navigation with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (state.hasChanges && state.isEditing) {
        e.preventDefault();
        e.returnValue = "У вас есть несохранённые изменения. Вы уверены, что хотите покинуть страницу?";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [state.hasChanges, state.isEditing]);

  return {
    ...state,
    editingValue,
    startEditing,
    cancelEditing,
    saveValue: (value: string) => saveValue(value, false),
    updateEditingValue,
  };
};